import { motion } from "framer-motion";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useResponsive } from "../../../hooks/useResponsive";
import Tooltip from "../../../components/Tooltip";
import { LockIcon } from "./icons/LockIcon";

export default function HeaderTab({
  tabs = [],
  activeTab = "",
  onTabChange,
  className = "",
  syncWithUrl = false,
  paramKey = "tab",
  defaultTab = tabs[0]?.id,
  layoutId,
}) {
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = React.useId();
  const effectiveLayoutId = layoutId || `header-tab-underline-${id}`;

  const isFirstMount = React.useRef(true);

  React.useEffect(() => {
    if (syncWithUrl && isFirstMount.current && tabs.length > 0) {
      const urlValue = searchParams.get(paramKey);
      const effectiveTab = urlValue || defaultTab;

      const matchedTab = tabs.find((t) => t.id === effectiveTab);

      if (matchedTab && !matchedTab.locked && effectiveTab !== activeTab) {
        onTabChange?.(effectiveTab);
      }
      isFirstMount.current = false;
    }
  }, [
    searchParams,
    syncWithUrl,
    paramKey,
    activeTab,
    onTabChange,
    tabs,
    defaultTab,
  ]);

  const handleTabClick = (tab) => {
    if (tab.locked) return;

    onTabChange?.(tab.id);

    if (syncWithUrl) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(paramKey, tab.id);
          return newParams;
        },
        { replace: true },
      );
    }
  };

  return (
    <div
      className={`relative flex items-center border-b border-[#F3F4F6] overflow-x-auto scrollbar-hide ${className} ${
        isMobile ? "gap-[12px]" : "gap-[20px]"
      }`}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const button = (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab)}
            className={`relative ${isMobile ? "h-[36px]" : "h-[40px]"} flex flex-col items-center justify-center pt-[8px] pb-[8px] focus:outline-none group shrink-0`}
          >
            <span
              className={`flex items-center gap-[4px] text-[12px] sm:text-[14px] leading-[20px] font-[500] transition-colors duration-200 ${
                isActive
                  ? "text-[#111827]"
                  : "text-[#636369] group-hover:text-[#111827]"
              }`}
            >
              {tab.label}
              {tab.locked && <LockIcon />}
            </span>
            {isActive && !tab.locked && (
              <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                <motion.div
                  layoutId={effectiveLayoutId}
                  className="w-[20px] sm:w-[24px] h-[3px] sm:h-[4px] bg-[#3c3c43] rounded-[2px]"
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 35,
                    mass: 1,
                  }}
                />
              </div>
            )}
          </button>
        );

        if (tab.locked && tab.tooltip) {
          return (
            <Tooltip key={tab.id} content={tab.tooltip} side="top">
              {button}
            </Tooltip>
          );
        }

        return button;
      })}
    </div>
  );
}
